import { cn } from '@neo/lib/utils'
import Quill from 'quill'
import Delta, { type Op } from 'quill-delta'
import 'quill/dist/quill.snow.css'
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef } from 'react'
import './mention-blot'
import type { MentionData } from './mention-blot'

export type { MentionData as NodeMentionData }

export interface VibeEnhancedInputRef {
  insertMention: (node: MentionData) => void
  getPlainText: () => string
  focus: () => void
  clear: () => void
}

interface VibeEnhancedInputProps {
  placeholder?: string
  /** Controlled value. Syncs external changes to Quill editor. */
  value: string
  onChange: (text: string) => void
  onMentionTrigger: (triggered: boolean) => void
  onKeyDown?: (e: KeyboardEvent) => void
  disabled?: boolean
  className?: string
}

export const VibeEnhancedInput = forwardRef<VibeEnhancedInputRef, VibeEnhancedInputProps>(
  (
    {
      placeholder = 'Describe the agentic flow you want to build...',
      value,
      onChange,
      onMentionTrigger,
      onKeyDown,
      disabled = false,
      className,
    },
    ref,
  ) => {
    const editorRef = useRef<HTMLDivElement>(null)
    const quillRef = useRef<Quill>()
    const isInternalChange = useRef(false)
    const mentionTriggerIndexRef = useRef<number | null>(null)
    const cursorPosAtTriggerRef = useRef<number | null>(null)

    const onChangeRef = useRef(onChange)
    const onMentionTriggerRef = useRef(onMentionTrigger)
    const onKeyDownRef = useRef(onKeyDown)

    useEffect(() => {
      onChangeRef.current = onChange
      onMentionTriggerRef.current = onMentionTrigger
      onKeyDownRef.current = onKeyDown
    })

    const getPlainText = useCallback((): string => {
      const quill = quillRef.current
      if (!quill) return ''

      const contents = quill.getContents()
      let text = ''

      contents.ops?.forEach((op: Op) => {
        if (typeof op.insert === 'string') {
          text += op.insert
        } else if (typeof op.insert === 'object' && op.insert && 'mention' in op.insert) {
          const mentionData = op.insert.mention as MentionData
          if (mentionData?.name) {
            text += `@${mentionData.name}`
          }
        }
      })

      return text.trim()
    }, [])

    const insertMention = useCallback(
      (node: MentionData) => {
        const quill = quillRef.current
        if (!quill) return

        let insertIndex: number

        // Use the stored trigger index and cursor position (set when @ was typed)
        if (mentionTriggerIndexRef.current !== null && cursorPosAtTriggerRef.current !== null) {
          insertIndex = mentionTriggerIndexRef.current
          const deleteLength = cursorPosAtTriggerRef.current - insertIndex
          if (deleteLength > 0) {
            quill.deleteText(insertIndex, deleteLength)
          }
          mentionTriggerIndexRef.current = null
          cursorPosAtTriggerRef.current = null
        } else {
          const selection = quill.getSelection()
          insertIndex = selection?.index ?? quill.getLength() - 1
        }

        isInternalChange.current = true
        quill.insertEmbed(insertIndex, 'mention', node, 'user')
        quill.insertText(insertIndex + 1, ' ', 'user')
        quill.setSelection(insertIndex + 2, 0)
        isInternalChange.current = false

        onChangeRef.current(getPlainText())
      },
      [getPlainText],
    )

    const clear = useCallback(() => {
      const quill = quillRef.current
      if (!quill) return
      isInternalChange.current = true
      quill.setText('')
      isInternalChange.current = false
    }, [])

    const focus = useCallback(() => {
      quillRef.current?.focus()
    }, [])

    useImperativeHandle(
      ref,
      () => ({
        insertMention,
        getPlainText,
        focus,
        clear,
      }),
      [insertMention, getPlainText, focus, clear],
    )

    // Quill reads the placeholder option only at construction, but renders it
    // from .ql-editor[data-placeholder] — a prop change must write the
    // attribute directly (the queue swaps copy while a run is busy).
    useEffect(() => {
      const quill = quillRef.current
      if (quill) quill.root.dataset.placeholder = placeholder
    }, [placeholder])

    useEffect(() => {
      if (!editorRef.current) return

      // Note: placeholder is set at construction; later prop changes are
      // applied by the data-placeholder effect above.
      const quill = new Quill(editorRef.current, {
        modules: {
          toolbar: false,
          keyboard: {
            bindings: {
              enter: {
                key: 'Enter',
                shiftKey: false,
                handler: () => {
                  return true
                },
              },
            },
          },
        },
        formats: ['mention'],
        placeholder,
        theme: 'snow',
        readOnly: disabled,
      })

      quill.on('text-change', (delta, _oldDelta, source) => {
        if (source !== 'user' || isInternalChange.current) return

        const selection = quill.getSelection()
        if (!selection) return

        // Check if @ was just typed by looking at delta (not getText which expands embeds)
        let atWasTyped = false
        for (const op of delta.ops || []) {
          if (op.insert === '@') {
            atWasTyped = true
            break
          }
        }

        if (atWasTyped) {
          // @ was typed, cursor is now after it
          const atIndex = selection.index - 1

          // Check character before @
          let validTrigger = atIndex === 0
          if (!validTrigger && atIndex > 0) {
            const beforeContents = quill.getContents(atIndex - 1, 1)
            if (beforeContents.ops?.[0]) {
              const before = beforeContents.ops[0].insert
              if (typeof before === 'string') {
                validTrigger = before === ' ' || before === '\n'
              } else {
                // before is an embed (mention), treat space after mention as valid
                validTrigger = true
              }
            }
          }

          if (validTrigger) {
            mentionTriggerIndexRef.current = atIndex
            cursorPosAtTriggerRef.current = selection.index
            onMentionTriggerRef.current(true)
          }
        } else if (mentionTriggerIndexRef.current !== null) {
          // Check if space/newline was typed to close popover
          let shouldClose = false
          for (const op of delta.ops || []) {
            if (
              typeof op.insert === 'string' &&
              (op.insert.includes(' ') || op.insert.includes('\n'))
            ) {
              shouldClose = true
              break
            }
          }

          if (shouldClose) {
            mentionTriggerIndexRef.current = null
            cursorPosAtTriggerRef.current = null
            onMentionTriggerRef.current(false)
          } else {
            cursorPosAtTriggerRef.current = selection.index
          }
        }

        onChangeRef.current(getPlainText())
      })

      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
          mentionTriggerIndexRef.current = null
          cursorPosAtTriggerRef.current = null
          onMentionTriggerRef.current(false)
        }
        onKeyDownRef.current?.(e)
      }

      // Strip all formatting from pasted content, keep only plain text
      quill.clipboard.addMatcher(Node.ELEMENT_NODE, (_node, delta) => {
        const plainTextDelta = new Delta()
        delta.ops.forEach((op: Op) => {
          if (typeof op.insert === 'string') {
            plainTextDelta.insert(op.insert)
          }
        })
        return plainTextDelta
      })

      quill.root.addEventListener('keydown', handleKeyDown)

      quillRef.current = quill

      return () => {
        quill.root.removeEventListener('keydown', handleKeyDown)
        quillRef.current = undefined
      }
    }, [])

    useEffect(() => {
      const quill = quillRef.current
      if (!quill) return
      quill.enable(!disabled)
    }, [disabled])

    useEffect(() => {
      const quill = quillRef.current
      if (!quill || isInternalChange.current) return

      const currentText = getPlainText()
      // Only sync when clearing or when value was set externally (e.g. pill click)
      // and the editor is not focused (user is not actively typing)
      const isFocused = quill.hasFocus()
      if (currentText !== value && (value === '' || !isFocused)) {
        isInternalChange.current = true
        if (value === '') {
          quill.setText('')
        } else {
          quill.setText(value)
          quill.setSelection(value.length, 0)
        }
        isInternalChange.current = false
      }
    }, [value, getPlainText])

    return (
      <div
        className={cn(
          'w-full rounded border border-st1 bg-background',
          'focus-within:ring-0 focus-within:outline-none overflow-hidden',
          '[&_.ql-container.ql-snow]:border-0 [&_.ql-container.ql-snow]:text-p-400',
          // No editor max-height here: quill's own height:100% chain fills this
          // wrapper, and the composer box in input.tsx owns the min/max clamp
          // (shared by auto-grow and the manual resize-y drag).
          '[&_.ql-editor]:min-h-[100px] [&_.ql-editor]:overflow-y-auto [&_.ql-editor]:subtle-scrollbar',
          '[&_.ql-editor]:px-3 [&_.ql-editor]:py-3',
          '[&_.ql-editor.ql-blank::before]:not-italic [&_.ql-editor.ql-blank::before]:text-tertiary',
          '[&_.ql-editor.ql-blank::before]:left-3',
          className,
        )}
        data-testid="vibe-agent-enhanced-input"
      >
        <div ref={editorRef} />
      </div>
    )
  },
)

VibeEnhancedInput.displayName = 'VibeEnhancedInput'
